import { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Field, TextInput } from "@/components/auth/Field";
import { Spinner } from "@/components/common/FullScreenLoader";
import { api, apiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post("/auth/password/forgot", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setError(apiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      eyebrow="Account recovery"
      title="Reset your password."
      subtitle="Enter the email on your BIZII account. If it exists, we will send a single-use link that expires in 60 minutes."
      footer={
        <Link to="/sign-in" data-testid="forgot-password-back-link" className="transition-colors hover:text-electric">
          ← Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div data-testid="forgot-password-success" className="space-y-4">
          <div className="flex items-center gap-3 rounded-md border border-electric/30 bg-electric/10 px-4 py-3 text-sm text-slate-100">
            <MailCheck className="h-5 w-5 flex-none text-electric" strokeWidth={1.75} />
            If an account exists for <span className="font-mono text-electric">{email}</span>, a reset link is on its way.
          </div>
          <p className="text-xs text-slate-500">
            Phase 0 uses a mock transactional email adapter — no real email is sent. Links appear in the development outbox.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" noValidate>
          <Field id="email" label="Email">
            <TextInput
              id="email"
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="forgot-password-email-input"
            />
          </Field>
          {error ? (
            <div role="alert" data-testid="forgot-password-error" className="bz-alert-error">
              {error}
            </div>
          ) : null}
          <button type="submit" disabled={busy || !email} data-testid="forgot-password-submit-btn" className="bz-btn-primary w-full">
            {busy ? <Spinner /> : null}
            Send reset link
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
